import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import PaymentGatewayApi from "../../../infrastructure/api/paymentGateway";
import PaymentStatusBadge from "./PaymentStatusBadge";
import {
  formatRial,
  mapSessionToBadgeState,
  normalizeUnpaidSummary,
  pick,
} from "./paymentGatewayUtils";

const PaymentSummaryWidget = ({ compact = false, highlightInvoiceId = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await PaymentGatewayApi.getUnpaidInvoicesSummary();
      const { error: err, data } = normalizeUnpaidSummary(res);
      if (err) {
        setError(err);
        setSummary(null);
      } else {
        setSummary(data);
      }
    } catch {
      setError("بارگذاری خلاصه پرداخت ناموفق بود.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const unpaidCount = Number(
    pick(summary, "unpaidInvoiceCount", "UnpaidInvoiceCount") ?? 0
  );
  const totalRemaining = pick(summary, "totalRemainingAmount", "TotalRemainingAmount");
  const latestStatus = pick(summary, "latestPaymentStatus", "LatestPaymentStatus");
  const latestStatusTitle = pick(
    summary,
    "latestPaymentStatusTitle",
    "LatestPaymentStatusTitle"
  );
  const unpaidInvoices =
    pick(summary, "unpaidInvoices", "UnpaidInvoices") ?? [];

  const badgeState = mapSessionToBadgeState(latestStatus);

  const goToInvoice = (invoiceId) => {
    if (invoiceId) {
      navigate(`/SLN/Customer/InvoicePayment/${invoiceId}`);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width="55%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rounded" height={36} />
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
        <Button size="small" onClick={loadSummary}>
          تلاش مجدد
        </Button>
      </Paper>
    );
  }

  if (unpaidCount === 0) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReceiptLongIcon color="success" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            فاکتور پرداخت‌نشده‌ای ندارید.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            فاکتورهای پرداخت‌نشده
          </Typography>
          <PaymentStatusBadge
            state={badgeState}
            label={latestStatusTitle || undefined}
          />
        </Stack>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            تعداد فاکتور
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {unpaidCount.toLocaleString("fa-IR")}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            مبلغ قابل پرداخت
          </Typography>
          <Typography variant="body1" fontWeight={700} color="primary.main">
            {formatRial(totalRemaining)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            آخرین وضعیت پرداخت
          </Typography>
          <Typography variant="body2">
            {latestStatusTitle || "—"}
          </Typography>
        </Box>

        {unpaidInvoices.length > 0 ? (
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              دسترسی سریع
            </Typography>
            {unpaidInvoices.slice(0, 5).map((inv) => {
              const id = pick(inv, "invoiceId", "InvoiceId");
              const isHighlighted = highlightInvoiceId && Number(highlightInvoiceId) === Number(id);
              return (
                <Button
                  key={id}
                  fullWidth
                  size="small"
                  variant={isHighlighted ? "contained" : "outlined"}
                  endIcon={<ChevronLeftIcon />}
                  onClick={() => goToInvoice(id)}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Box sx={{ textAlign: "right", flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {pick(inv, "title", "Title") ||
                        pick(inv, "invoiceNumber", "InvoiceNumber") ||
                        `فاکتور #${id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRial(pick(inv, "remainingAmount", "RemainingAmount"))}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

PaymentSummaryWidget.propTypes = {
  compact: PropTypes.bool,
  highlightInvoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PaymentSummaryWidget;
